import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HolidayEggBanner — shows the active holiday egg (if any) with a "Claim now"
 * button. Fires a one-time confetti burst the first time a user sees a given
 * holiday window each year (key locked in localStorage).
 */
export default function HolidayEggBanner() {
  const [active, setActive] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/holiday/active`);
        if (cancelled) return;
        const a = data?.active || null;
        setActive(a);
        // Fire confetti once per holiday window per browser
        if (a) {
          const seenKey = `omni:holiday-seen:${a.key}:${a.active_date}`;
          if (!localStorage.getItem(seenKey)) {
            setShowConfetti(true);
            localStorage.setItem(seenKey, "1");
            // Auto-stop after 4.5s
            setTimeout(() => setShowConfetti(false), 4500);
          }
        }
      } catch {
        if (!cancelled) setActive(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Confetti pieces (themed to the holiday)
  const confetti = useMemo(() => {
    const t = active?.theme || {};
    const colors = t.tricolor
      ? ["#DC2626", "#FFFFFF", "#1E40AF", "#FBBF24"]
      : t.rainbow
      ? ["#FF0080", "#FFD700", "#00BFFF", "#8A2BE2", "#00FF7F", "#FF1493"]
      : [t.ring || "#D4AF37", t.highlight || "#FFFFFF", t.base || "#A855F7", "#FBBF24"];
    return Array.from({ length: 40 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 1.5,
      duration: 2.8 + Math.random() * 1.8,
      color: colors[i % colors.length],
      rotate: Math.random() * 360,
    }));
  }, [active]);

  if (!active || dismissed) return null;

  const theme = active.theme || {};
  const isTricolor = theme.tricolor;
  const isRainbow = theme.rainbow;
  const bgStyle = isRainbow
    ? {
        background: "linear-gradient(90deg, #FF0080, #FFD700, #00FF7F, #00BFFF, #8A2BE2, #FF1493)",
        backgroundSize: "300% 100%",
        animation: "omni-bg-rotate 6s linear infinite",
      }
    : isTricolor
    ? {
        background: `linear-gradient(90deg, ${theme.shadow} 0%, ${theme.base} 50%, ${theme.shadow} 100%)`,
      }
    : {
        background: `linear-gradient(90deg, ${theme.shadow || "#0A0A0A"} 0%, ${theme.base || "#1a0a0a"} 100%)`,
      };

  return (
    <div
      data-testid="holiday-banner"
      className="relative w-full overflow-hidden border-y-2 mt-[100px] md:mt-[108px]"
      style={{ borderColor: theme.ring || "#D4AF37", ...bgStyle }}
    >
      <div className="absolute inset-0 bg-black/40" />
      {/* 🎊 One-time confetti burst per holiday window per browser */}
      {showConfetti && (
        <div
          data-testid="holiday-confetti"
          className="absolute inset-0 pointer-events-none overflow-hidden"
          aria-hidden
        >
          {confetti.map((c) => (
            <div
              key={c.id}
              className="confetti-piece"
              style={{
                left: `${c.left}%`,
                background: c.color,
                animationDelay: `${c.delay}s`,
                animationDuration: `${c.duration}s`,
                transform: `rotate(${c.rotate}deg)`,
              }}
            />
          ))}
        </div>
      )}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4 flex-wrap">
        <div className="text-4xl">{active.icon || "🥚"}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/80 font-bold">
            Limited · {active.tier_name} Egg · {active.letter_grade}
          </div>
          <div className="font-display text-xl md:text-2xl text-white leading-tight">
            {active.label} is live
          </div>
          {active.blurb && (
            <div className="text-xs md:text-sm text-white/90 mt-1 line-clamp-2">
              {active.blurb}
            </div>
          )}
          <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">
            Auto-claimed on your next daily sign-in · drops {active.window_start} → {active.window_end} Phoenix Tokens
          </div>
        </div>
        <Link
          to="/nest"
          data-testid="holiday-banner-cta"
          className="bg-white text-black font-bold uppercase tracking-widest text-xs px-4 py-2 hover:bg-yellow-300 transition-colors"
        >
          View My Nest →
        </Link>
        <button
          data-testid="holiday-banner-dismiss"
          onClick={() => setDismissed(true)}
          className="text-white/60 hover:text-white text-sm leading-none"
          title="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
